import React from 'react';
import { motion } from 'framer-motion';
import { Play, CheckCircle2, Zap, Users, BookOpen, BrainCircuit } from 'lucide-react';
export function Hero() {
  return <section id="inicio" className="relative pt-28 pb-12 lg:pt-36 lg:pb-20 overflow-hidden bg-white" data-id="element-104">
      {/* Background decorative elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden -z-10 pointer-events-none" data-id="element-105">
        <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] rounded-full bg-link-light/60 blur-3xl" data-id="element-106" />
        <div className="absolute bottom-[-10%] left-[-10%] w-[600px] h-[600px] rounded-full bg-blue-50/50 blur-3xl" data-id="element-107" />
        {/* Subtle grid pattern */}
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iMSIgY3k9IjEiIHI9IjEiIGZpbGw9IiNFMkU4RjAiLz48L3N2Zz4=')] opacity-50" data-id="element-108" />
      </div>

      <div className="container mx-auto px-4 md:px-6" data-id="element-109">
        <div className="flex flex-col items-center gap-12" data-id="element-110">
          {/* Top Content */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="max-w-3xl flex flex-col items-center text-center mx-auto" data-id="element-111">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-link-light text-link-blue text-sm font-semibold mb-6" data-id="element-112">
              <span className="relative flex h-2 w-2" data-id="element-113">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-link-blue opacity-75" data-id="element-114"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-link-blue" data-id="element-115"></span>
              </span>
              La academia de marketing digital del futuro
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-link-dark leading-[1.1] mb-6 font-heading" data-id="element-116">
              Aprende marketing digital con IA, cursos prácticos y una comunidad
              que te acompaña
            </h1>

            <p className="text-lg md:text-xl text-gray-600 mb-8 leading-relaxed" data-id="element-117">
              Link Academy te ayuda a pasar de la teoría a la acción: crea
              estrategias, lanza campañas, automatiza procesos y aprende con
              mentorías reales dentro de Skool.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-10 justify-center" data-id="element-118">
              <a href="#unete" className="inline-flex justify-center items-center bg-link-blue hover:bg-blue-700 text-white px-8 py-4 rounded-full font-semibold text-lg transition-all shadow-lg shadow-link-blue/30 hover:shadow-link-blue/50 hover:-translate-y-1" data-id="element-119">
                Únete a Skool ahora
              </a>
              <a href="#alice-app" className="inline-flex justify-center items-center bg-white border-2 border-gray-200 hover:border-link-blue hover:text-link-blue text-link-dark px-8 py-4 rounded-full font-semibold text-lg transition-all" data-id="element-120">
                Ver cómo funciona Alice
              </a>
            </div>

            <div className="flex flex-wrap justify-center gap-y-3 gap-x-6 text-sm font-medium text-gray-600" data-id="element-121">
              <div className="flex items-center gap-2" data-id="element-122">
                <CheckCircle2 className="text-link-blue" size={18} data-id="element-123" />
                <span data-id="element-124">Acceso inmediato</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-125">
                <CheckCircle2 className="text-link-blue" size={18} data-id="element-126" />
                <span data-id="element-127">Cursos paso a paso</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-128">
                <CheckCircle2 className="text-link-blue" size={18} data-id="element-129" />
                <span data-id="element-130">Comunidad en español</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-131">
                <CheckCircle2 className="text-link-blue" size={18} data-id="element-132" />
                <span data-id="element-133">Herramientas con IA</span>
              </div>
            </div>
          </motion.div>

          {/* Right Visuals */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="relative w-full max-w-2xl h-[420px] md:h-[480px] flex items-center justify-center mx-auto" data-id="element-134">
            {/* Main Video Placeholder */}
            <div className="relative z-10 w-full max-w-md aspect-[4/3] bg-gradient-to-br from-link-navy to-link-blue rounded-2xl shadow-2xl overflow-hidden border-4 border-white flex flex-col items-center justify-center text-white" data-id="element-135">
              <img src="https://cdn.magicpatterns.com/uploads/bcyWYhJxpKboTXdtVC9uRD/image.png" alt="Vista previa de Link Academy en Skool" className="absolute inset-0 w-full h-full object-cover" data-id="element-136" />
            </div>

            {/* Floating Mockup (Alice App) */}
            <motion.div animate={{
            y: [0, -15, 0]
          }} transition={{
            repeat: Infinity,
            duration: 6,
            ease: 'easeInOut'
          }} className="absolute -right-4 md:-right-12 bottom-10 z-20 w-44 h-52 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden flex flex-col" data-id="element-137">
              <div className="bg-link-light px-3 py-2 border-b border-gray-100 flex items-center gap-2" data-id="element-138">
                <BrainCircuit className="text-link-blue" size={14} data-id="element-139" />
                <span className="text-xs font-bold text-link-dark" data-id="element-140">
                  Alice App
                </span>
              </div>
              <div className="p-3 flex-1 flex flex-col gap-2" data-id="element-141">
                <div className="w-3/4 h-1.5 bg-gray-100 rounded-full" data-id="element-142" />
                <div className="w-full h-1.5 bg-gray-100 rounded-full" data-id="element-143" />
                <div className="w-5/6 h-1.5 bg-gray-100 rounded-full" data-id="element-144" />
                <div className="mt-auto w-full h-12 bg-link-light rounded-lg border border-blue-100 flex items-center justify-center" data-id="element-145">
                  <span className="text-[10px] text-link-blue font-medium" data-id="element-146">
                    Generando estrategia...
                  </span>
                </div>
              </div>
            </motion.div>

            {/* Floating Cards */}
            <motion.div animate={{
            y: [0, 10, 0]
          }} transition={{
            repeat: Infinity,
            duration: 5,
            ease: 'easeInOut',
            delay: 1
          }} className="absolute -left-4 md:-left-8 top-20 z-20 bg-white p-3 rounded-xl shadow-lg border border-gray-100 flex items-center gap-3" data-id="element-147">
              <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center" data-id="element-148">
                <Zap className="text-link-blue" size={20} data-id="element-149" />
              </div>
              <span className="font-semibold text-sm text-link-dark pr-2" data-id="element-150">
                Estrategia en minutos
              </span>
            </motion.div>

            <motion.div animate={{
            y: [0, -10, 0]
          }} transition={{
            repeat: Infinity,
            duration: 5.5,
            ease: 'easeInOut',
            delay: 0.5
          }} className="absolute left-10 bottom-0 z-20 bg-white p-3 rounded-xl shadow-lg border border-gray-100 flex items-center gap-3" data-id="element-151">
              <div className="w-10 h-10 rounded-full bg-indigo-50 flex items-center justify-center" data-id="element-152">
                <BookOpen className="text-indigo-600" size={20} data-id="element-153" />
              </div>
              <span className="font-semibold text-sm text-link-dark pr-2" data-id="element-154">
                Cursos prácticos
              </span>
            </motion.div>

            <motion.div animate={{
            y: [0, 12, 0]
          }} transition={{
            repeat: Infinity,
            duration: 6.5,
            ease: 'easeInOut',
            delay: 2
          }} className="absolute right-2 md:right-4 -top-10 z-30 bg-white p-3 rounded-xl shadow-lg border border-gray-100 flex items-center gap-3" data-id="element-155">
              <div className="w-10 h-10 rounded-full bg-green-50 flex items-center justify-center" data-id="element-156">
                <Users className="text-green-600" size={20} data-id="element-157" />
              </div>
              <span className="font-semibold text-sm text-link-dark pr-2" data-id="element-158">
                Mentorías en vivo
              </span>
            </motion.div>

            <motion.div animate={{
            y: [0, -8, 0]
          }} transition={{
            repeat: Infinity,
            duration: 4.5,
            ease: 'easeInOut',
            delay: 1.5
          }} className="absolute -right-8 top-1/2 z-0 bg-white p-3 rounded-xl shadow-lg border border-gray-100 flex items-center gap-3" data-id="element-159">
              <div className="w-10 h-10 rounded-full bg-purple-50 flex items-center justify-center" data-id="element-160">
                <BrainCircuit className="text-purple-600" size={20} data-id="element-161" />
              </div>
              <span className="font-semibold text-sm text-link-dark pr-2" data-id="element-162">
                IA aplicada a tu negocio
              </span>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>;
}